import { useState } from "react"
import type { ComponentProps, SubmitEvent } from "react"
import BulkForm, {
  useBulkRows,
} from "@/v2-backoffice-client/components/bulkForm"
import TenantInvitationCreateTable from "@/v2-backoffice-client/tenant/invitation/create/TenantInvitationCreateTable"
import InvitationCreateProjectFields from "@/v2-backoffice-client/invitation/create/InvitationCreateProjectFields"
import InvitationCreateFooter from "@/v2-backoffice-client/invitation/create/InvitationCreateFooter"
import InvitationCreateTemplateField from "@/v2-backoffice-client/invitation/create/InvitationCreateTemplateField"
import {
  type InvitationProjectValues,
  useInvitationProjectValues,
} from "@/v2-backoffice-client/invitation/create/invitation.create.hooks"
import CrmCreateContactCheckbox from "@/v2-backoffice-client/crm/CrmCreateContactCheckbox"
import { useContactEmailChecks } from "@/v2-backoffice-client/crm/crm.contactChecks.hooks"
import type {
  InvitationRow,
  InvitationEmailTemplateLiteral,
} from "@/v2-backoffice-client/invitation/invitation.constants"
import {
  DEFAULT_LANGUAGE,
  DEFAULT_EMAIL_TEMPLATE,
  BULK_ROWS_OPTIONS,
} from "@/v2-backoffice-client/invitation/invitation.constants"
import { describeExistingInvitees } from "@/v2-backoffice-client/invitation/invitation.utils"

export type TenantInvitationCreateFormData = InvitationProjectValues & {
  rows: InvitationRow[]
  SendImmediately: boolean
  Language: string
  EmailTemplate: InvitationEmailTemplateLiteral
  // Create HubSpot contacts for invitees who have none (existing contacts
  // get the invitation block either way).
  CreateContact: boolean
}

export type TenantInvitationCreateFormProps = Omit<
  ComponentProps<"form">,
  "onSubmit"
> & {
  tenantId: number
  onSubmit(
    data: TenantInvitationCreateFormData,
    e: SubmitEvent<HTMLFormElement>,
  ): void
}

function TenantInvitationCreateForm(props: TenantInvitationCreateFormProps) {
  const { tenantId, onSubmit, ...formProps } = props

  const project = useInvitationProjectValues()
  const bulk = useBulkRows<InvitationRow>(BULK_ROWS_OPTIONS)
  const [sendImmediately, setSendImmediately] = useState(true)
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE)
  const [emailTemplate, setEmailTemplate] =
    useState<InvitationEmailTemplateLiteral>(DEFAULT_EMAIL_TEMPLATE)
  const [createContact, setCreateContact] = useState(true)

  // One batched Backoffice + HubSpot check for every complete email in the
  // table; each row's HubSpot column and the checkbox read from it.
  const checks = useContactEmailChecks(bulk.rows.map((row) => row.Email))

  const validRows = bulk.rows.filter((row) => {
    return row.Email.trim().length > 0
  })

  return (
    <BulkForm
      onPaste={bulk.handlePaste}
      onSubmit={(e) => {
        onSubmit(
          {
            ...project.values,
            rows: validRows,
            SendImmediately: sendImmediately,
            Language: language,
            EmailTemplate: emailTemplate,
            CreateContact: createContact,
          },
          e,
        )
      }}
      {...formProps}
    >
      <InvitationCreateProjectFields
        className="invitation-create-form-project-section"
        tenantId={tenantId}
        projectId={project.projectId}
        onProjectIdChange={project.setProjectId}
        autoCreateProject={project.autoCreateProject}
        onAutoCreateProjectChange={project.setAutoCreateProject}
        countryCode={project.countryCode}
        onCountryCodeChange={project.setCountryCode}
      />

      <BulkForm.Actions
        onAddRow={bulk.addRow}
        onReset={bulk.resetRows}
        count={validRows.length}
        countLabel="invitation(s)"
        hint="Tip: you can paste a table to populate multiple rows at once"
      />

      <TenantInvitationCreateTable
        rows={bulk.rows}
        onUpdateRow={bulk.updateRow}
        onRemoveRow={bulk.removeRow}
        checks={checks}
        createContact={createContact}
      />

      <InvitationCreateTemplateField
        value={emailTemplate}
        onChange={setEmailTemplate}
      />

      {/* A fixed slot, so the warning appearing never moves the form. */}
      <span className="bulk-form-warning mt-3 block h-5 truncate">
        {describeExistingInvitees(checks.existingUserCount)}
      </span>

      <CrmCreateContactCheckbox
        className="mt-3 rounded-md border border-border px-3"
        checked={createContact}
        onCheckedChange={setCreateContact}
        counts={
          checks.answered
            ? {
                new: checks.newCount,
                contacts: checks.existingContactCount,
                users: checks.existingUserCount,
              }
            : undefined
        }
      />

      <InvitationCreateFooter
        sendImmediately={sendImmediately}
        onSendImmediatelyChange={setSendImmediately}
        language={language}
        onLanguageChange={setLanguage}
        hasValidRows={validRows.length > 0}
      />
    </BulkForm>
  )
}

export default TenantInvitationCreateForm
